import React, { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, FormGroup, IconButton, MenuItem, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead,
  Tab, TableRow, Tabs, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import EditOutlined from '@mui/icons-material/EditOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { apiHost } from '../../utils/apiHost';
import { konfirmasi, notif } from '../../utils/dialog';

// Kelola promo otomatis (admin cabang: cabangnya; superadmin: semua + promo semua cabang).
// Server: controller/promoController.js, perhitungan di utils/promo.js. Promo dihitung otomatis saat transaksi.
const base = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  return `${protocol}://${apiHost().replace(/^https?:\/\//, '')}`;
};
const ambil = (url) => axios.get(url, { withCredentials: true }).then((r) => r.data);
const rupiah = (v) => `Rp ${Math.round(Number(v) || 0).toLocaleString('id-ID')}`;
const NAMA_HARI = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
const tglPendek = (t) => (t ? new Date(`${t}T00:00:00`).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const teksPotongan = (p) => (p.jenis === 'persen'
  ? `${Number(p.nilai)}%${p.maksPotongan ? ` (maks ${rupiah(p.maksPotongan)})` : ''}`
  : rupiah(p.nilai));

const teksJadwal = (p) => {
  const bagian = [];
  if (p.tanggalMulai && p.tanggalSelesai) bagian.push(`${tglPendek(p.tanggalMulai)} – ${tglPendek(p.tanggalSelesai)}`);
  else if (p.tanggalMulai) bagian.push(`mulai ${tglPendek(p.tanggalMulai)}`);
  else if (p.tanggalSelesai) bagian.push(`sampai ${tglPendek(p.tanggalSelesai)}`);
  bagian.push(Array.isArray(p.hari) && p.hari.length ? p.hari.map((h) => NAMA_HARI[h]).join(', ') : 'Setiap hari');
  bagian.push(p.jamMulai && p.jamSelesai ? `${p.jamMulai}–${p.jamSelesai}` : 'Sepanjang hari');
  return bagian;
};

const kosong = {
  uuid: null, nama: '', deskripsi: '', jenis: 'persen', nilai: '', maksPotongan: '', sasaran: 'semua', sasaranUuids: [], minBelanja: '',
  tanggalMulai: '', tanggalSelesai: '', hari: [], jamMulai: '', jamSelesai: '', aktif: true, cabanguuid: '',
  tipe: 'otomatis', kode: '', ulangTahun: 'hari', batasTotal: '', batasPerPelanggan: '',
};

const dariPromo = (p) => ({
  uuid: p.uuid, nama: p.nama, deskripsi: p.deskripsi || '', jenis: p.jenis, nilai: String(p.nilai), maksPotongan: p.maksPotongan ? String(p.maksPotongan) : '',
  sasaran: p.sasaran, sasaranUuids: p.sasaranUuids || [], minBelanja: p.minBelanja ? String(p.minBelanja) : '',
  tanggalMulai: p.tanggalMulai || '', tanggalSelesai: p.tanggalSelesai || '', hari: p.hari || [], jamMulai: p.jamMulai || '', jamSelesai: p.jamSelesai || '',
  aktif: p.aktif, cabanguuid: p.cabanguuid || '',
  tipe: p.tipe || 'otomatis', kode: p.kode || '', ulangTahun: p.ulangTahun || 'hari',
  batasTotal: p.batasTotal ? String(p.batasTotal) : '', batasPerPelanggan: p.batasPerPelanggan ? String(p.batasPerPelanggan) : '',
});

const keBody = (f, superadmin) => ({
  nama: f.nama, deskripsi: f.deskripsi, jenis: f.jenis, nilai: Number(f.nilai),
  maksPotongan: f.jenis === 'persen' && f.maksPotongan !== '' ? Number(f.maksPotongan) : null,
  sasaran: f.sasaran, sasaranUuids: f.sasaran === 'semua' ? [] : f.sasaranUuids,
  minBelanja: f.minBelanja === '' ? 0 : Number(f.minBelanja),
  tanggalMulai: f.tanggalMulai || null, tanggalSelesai: f.tanggalSelesai || null,
  hari: f.hari, jamMulai: f.jamMulai || null, jamSelesai: f.jamSelesai || null, aktif: f.aktif,
  tipe: f.tipe, ...(f.tipe === 'kupon' ? { kode: String(f.kode || '').trim().toUpperCase() } : {}), ...(f.tipe === 'ulang_tahun' ? { ulangTahun: f.ulangTahun } : {}),
  batasTotal: f.batasTotal === '' ? null : Number(f.batasTotal), batasPerPelanggan: f.batasPerPelanggan === '' ? null : Number(f.batasPerPelanggan),
  ...(superadmin ? { cabanguuid: f.cabanguuid || '' } : {}),
});

const PromoForm = ({ open, awal, superadmin, daftarCabang, onClose, onSimpan }) => {
  const [f, setF] = useState(kosong);
  const [galat, setGalat] = useState('');
  const [proses, setProses] = useState(false);
  useEffect(() => { if (open) { setF(awal || kosong); setGalat(''); } }, [open, awal]);

  const cabangPilihan = superadmin ? f.cabanguuid : '';
  const { data: pilihan } = useSWR(open ? `${base()}/promo/pilihan${cabangPilihan ? `?cabang=${cabangPilihan}` : ''}` : null, ambil, { revalidateOnFocus: false });
  const kategori = pilihan?.data?.kategori || [];
  const produk = pilihan?.data?.produk || [];
  const opsi = f.sasaran === 'kategori' ? kategori : produk.map((p) => ({ uuid: p.uuid, nama: `${p.nama} (${rupiah(p.harga)})` }));
  const terpilih = opsi.filter((o) => f.sasaranUuids.includes(o.uuid));
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const semuaCabang = superadmin && !f.cabanguuid;

  const simpan = async () => {
    setProses(true); setGalat('');
    try {
      const body = keBody(f, superadmin);
      if (f.uuid) await axios.put(`${base()}/promo/${f.uuid}`, body, { withCredentials: true });
      else await axios.post(`${base()}/promo`, body, { withCredentials: true });
      onSimpan(f.uuid ? 'Promo diperbarui' : 'Promo dibuat');
      onClose();
    } catch (e) {
      setGalat(e?.response?.data?.message || e?.message || 'Gagal menyimpan promo');
    } finally {
      setProses(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !proses && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>{f.uuid ? 'Ubah promo' : 'Promo baru'}</DialogTitle>
      <DialogContent dividers>
        {galat && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGalat('')}>{galat}</Alert>}
        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField label="Nama promo" size="small" value={f.nama} onChange={(e) => set('nama', e.target.value)} inputProps={{ maxLength: 100 }} placeholder="mis. Happy Hour Minuman" />
          <TextField label="Keterangan (opsional)" size="small" value={f.deskripsi} onChange={(e) => set('deskripsi', e.target.value)} inputProps={{ maxLength: 255 }} />
          <Box>
            <Typography variant="caption" color="text.secondary">Jenis promo</Typography>
            <ToggleButtonGroup size="small" exclusive fullWidth value={f.tipe} onChange={(_, v) => v && set('tipe', v)} sx={{ mt: 0.5 }}>
              <ToggleButton value="otomatis">Otomatis</ToggleButton>
              <ToggleButton value="kupon">Kupon (kode)</ToggleButton>
              <ToggleButton value="ulang_tahun">Ulang tahun</ToggleButton>
            </ToggleButtonGroup>
            {f.tipe === 'otomatis' && <Typography variant="caption" color="text.secondary">Berlaku sendiri bila syaratnya terpenuhi.</Typography>}
            {f.tipe === 'kupon' && (
              <TextField
                size="small" fullWidth sx={{ mt: 1.5 }} label="Kode kupon" value={f.kode} onChange={(e) => set('kode', e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
                inputProps={{ maxLength: 20 }} placeholder="mis. HEMAT10" helperText="3-20 karakter (huruf, angka, - atau _). Kasir mengetik kode ini; ditumpuk di atas promo otomatis."
              />
            )}
            {f.tipe === 'ulang_tahun' && (
              <Box sx={{ mt: 1.5 }}>
                <ToggleButtonGroup size="small" exclusive value={f.ulangTahun} onChange={(_, v) => v && set('ulangTahun', v)}>
                  <ToggleButton value="hari">Hari H saja</ToggleButton>
                  <ToggleButton value="bulan">Sepanjang bulan ulang tahun</ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary" display="block">
                  Otomatis, tetapi hanya bila kasir memilih pelanggan terdaftar yang tanggal lahirnya cocok (isi tanggal lahir di data Pelanggan).
                </Typography>
              </Box>
            )}
          </Box>
          {superadmin && (
            <TextField select size="small" label="Berlaku di" value={f.cabanguuid} onChange={(e) => setF((s) => ({ ...s, cabanguuid: e.target.value, sasaran: e.target.value ? s.sasaran : (s.sasaran === 'kategori' ? 'semua' : s.sasaran), sasaranUuids: [] }))}>
              <MenuItem value="">Semua cabang</MenuItem>
              {daftarCabang.map((c) => <MenuItem key={c.uuid} value={c.uuid}>{c.namacabang}</MenuItem>)}
            </TextField>
          )}

          <Box>
            <Typography variant="caption" color="text.secondary">Potongan</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
              <ToggleButtonGroup size="small" exclusive value={f.jenis} onChange={(_, v) => v && set('jenis', v)}>
                <ToggleButton value="persen">Persen (%)</ToggleButton>
                <ToggleButton value="nominal">Nominal (Rp)</ToggleButton>
              </ToggleButtonGroup>
              <TextField size="small" type="number" label={f.jenis === 'persen' ? 'Diskon (%)' : 'Diskon (Rp)'} value={f.nilai} onChange={(e) => set('nilai', e.target.value)} inputProps={{ min: 0 }} sx={{ width: 150 }} />
              {f.jenis === 'persen' && (
                <TextField size="small" type="number" label="Maks potongan (Rp)" value={f.maksPotongan} onChange={(e) => set('maksPotongan', e.target.value)} inputProps={{ min: 0 }} helperText="Kosong = tanpa batas" sx={{ width: 190 }} />
              )}
            </Box>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">Berlaku untuk</Typography>
            <ToggleButtonGroup size="small" exclusive fullWidth value={f.sasaran} onChange={(_, v) => v && setF((s) => ({ ...s, sasaran: v, sasaranUuids: [] }))} sx={{ mt: 0.5 }}>
              <ToggleButton value="semua">Semua produk</ToggleButton>
              <ToggleButton value="kategori" disabled={semuaCabang}>Kategori</ToggleButton>
              <ToggleButton value="produk">Produk tertentu</ToggleButton>
            </ToggleButtonGroup>
            {semuaCabang && <Typography variant="caption" color="text.secondary">Promo semua cabang tidak bisa memakai kategori (kategori milik tiap cabang).</Typography>}
            {f.sasaran !== 'semua' && (
              <Autocomplete
                multiple size="small" sx={{ mt: 1.5 }} options={opsi} value={terpilih} getOptionLabel={(o) => o.nama} isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
                onChange={(_, v) => set('sasaranUuids', v.map((x) => x.uuid))} limitTags={4}
                renderInput={(params) => <TextField {...params} label={f.sasaran === 'kategori' ? 'Pilih kategori' : 'Pilih produk'} />}
              />
            )}
          </Box>

          <TextField size="small" type="number" label="Minimal belanja (Rp)" value={f.minBelanja} onChange={(e) => set('minBelanja', e.target.value)} inputProps={{ min: 0 }} helperText="Dihitung dari total belanja sebelum promo. Kosong = tanpa minimal." />

          <Box>
            <Typography variant="caption" color="text.secondary">Periode (opsional)</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
              <TextField size="small" type="date" label="Mulai" InputLabelProps={{ shrink: true }} value={f.tanggalMulai} onChange={(e) => set('tanggalMulai', e.target.value)} />
              <TextField size="small" type="date" label="Selesai (termasuk)" InputLabelProps={{ shrink: true }} value={f.tanggalSelesai} onChange={(e) => set('tanggalSelesai', e.target.value)} />
            </Box>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">Hari berlaku (kosong = setiap hari)</Typography>
            <FormGroup row>
              {NAMA_HARI.map((n, i) => (
                <FormControlLabel
                  key={n} label={n}
                  control={<Checkbox size="small" checked={f.hari.includes(i)} onChange={(e) => set('hari', e.target.checked ? [...f.hari, i].sort() : f.hari.filter((h) => h !== i))} />}
                />
              ))}
            </FormGroup>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">Jam berlaku (zona waktu cabang; boleh melewati tengah malam)</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
              <TextField size="small" type="time" label="Dari jam" InputLabelProps={{ shrink: true }} value={f.jamMulai} onChange={(e) => set('jamMulai', e.target.value)} />
              <TextField size="small" type="time" label="Sampai jam" InputLabelProps={{ shrink: true }} value={f.jamSelesai} onChange={(e) => set('jamSelesai', e.target.value)} />
              {(f.jamMulai || f.jamSelesai) && <Button size="small" onClick={() => setF((s) => ({ ...s, jamMulai: '', jamSelesai: '' }))}>Hapus jam</Button>}
            </Box>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">Batas pemakaian (opsional; transaksi yang dibatalkan tidak dihitung)</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
              <TextField size="small" type="number" label="Total pemakaian" value={f.batasTotal} onChange={(e) => set('batasTotal', e.target.value)} inputProps={{ min: 1 }} helperText="Kosong = tanpa batas" sx={{ width: 180 }} />
              <TextField size="small" type="number" label="Per pelanggan" value={f.batasPerPelanggan} onChange={(e) => set('batasPerPelanggan', e.target.value)} inputProps={{ min: 1 }} helperText="Kasir wajib memilih pelanggan" sx={{ width: 180 }} />
            </Box>
          </Box>

          <FormControlLabel control={<Switch checked={f.aktif} onChange={(e) => set('aktif', e.target.checked)} />} label="Promo aktif" />
          <Alert severity="info" icon={false} sx={{ fontSize: 13 }}>
            Promo otomatis yang cocok sekaligus tidak ditumpuk (dipakai satu dengan potongan terbesar). Kupon BOLEH ditumpuk di atas promo otomatis
            (dihitung dari total setelah promo otomatis). PPN dihitung dari total setelah semua potongan.
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={proses}>Batal</Button>
        <Button variant="contained" onClick={simpan} disabled={proses || !f.nama.trim() || !f.nilai || (f.tipe === 'kupon' && f.kode.trim().length < 3)}>{proses ? 'Menyimpan…' : 'Simpan'}</Button>
      </DialogActions>
    </Dialog>
  );
};

const tglIso = (d) => { const p = (x) => String(x).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };

// Laporan pemakaian promo pada transaksi lunas (GET /promo/laporan).
const LaporanPromo = ({ cabang }) => {
  const [dari, setDari] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 29); return tglIso(d); });
  const [sampai, setSampai] = useState(() => tglIso(new Date()));
  const query = new URLSearchParams({ dari, sampai, ...(cabang && { cabang }) }).toString();
  const { data, error, isLoading } = useSWR(`${base()}/promo/laporan?${query}`, ambil, { revalidateOnFocus: false });
  const list = Array.isArray(data?.data) ? data.data : [];
  const r = data?.ringkasan || {};
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <TextField size="small" type="date" label="Dari" InputLabelProps={{ shrink: true }} value={dari} onChange={(e) => setDari(e.target.value)} />
        <TextField size="small" type="date" label="Sampai" InputLabelProps={{ shrink: true }} value={sampai} onChange={(e) => setSampai(e.target.value)} />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        {[
          ['Transaksi berpromo', `${r.transaksiBerpromo ?? 0} dari ${r.transaksiSemua ?? 0}`, 'text.primary'],
          ['Porsi transaksi', `${r.persenBerpromo ?? 0}%`, 'info.main'],
          ['Total potongan', rupiah(r.totalPotongan), 'warning.dark'],
          ['Omzet transaksi berpromo', rupiah(r.omzetBerpromo), 'success.main'],
        ].map(([judul, nilai, warna]) => (
          <Card key={judul} variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">{judul}</Typography>
              <Typography variant="h6" color={warna} fontWeight={700}>{nilai}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error?.response?.data?.message || 'Gagal memuat laporan promo'}</Alert>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Promo</TableCell>
              <TableCell align="right">Dipakai</TableCell>
              <TableCell align="right">Total potongan</TableCell>
              <TableCell align="right">Rata-rata potongan</TableCell>
              <TableCell align="right">Omzet setelah promo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={5}>Memuat…</TableCell></TableRow>}
            {!isLoading && list.length === 0 && <TableRow><TableCell colSpan={5}>Belum ada transaksi berpromo pada rentang ini.</TableCell></TableRow>}
            {list.map((x) => (
              <TableRow key={x.promouuid} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={700}>{x.nama}</Typography>
                  {!x.masihAda && <Chip size="small" label="Sudah dihapus" sx={{ mt: 0.5 }} />}
                </TableCell>
                <TableCell align="right">{x.jumlah}×</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{rupiah(x.potongan)}</TableCell>
                <TableCell align="right">{rupiah(x.rataPotongan)}</TableCell>
                <TableCell align="right">{rupiah(x.omzet)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const PromoManager = () => {
  const { user } = useSelector((state) => state.auth);
  const superadmin = user?.role === 'superadmin';
  const [cabang, setCabang] = useState('');
  const [form, setForm] = useState({ open: false, awal: null });
  const [tab, setTab] = useState(0);
  const query = cabang ? `?cabang=${cabang}` : '';
  const { data, error, isLoading, mutate } = useSWR(`${base()}/promo${query}`, ambil, { revalidateOnFocus: false });
  const { data: cabangData } = useSWR(superadmin ? `${base()}/cabangbyrole?bentuk=objek` : null, ambil);
  const { data: pilihan } = useSWR(`${base()}/promo/pilihan${cabang ? `?cabang=${cabang}` : ''}`, ambil, { revalidateOnFocus: false });
  const daftarCabang = Array.isArray(cabangData?.data) ? cabangData.data : [];
  const list = Array.isArray(data?.data) ? data.data : [];
  const r = data?.ringkasan || {};
  const namaKategori = useMemo(() => Object.fromEntries((pilihan?.data?.kategori || []).map((k) => [k.uuid, k.nama])), [pilihan]);
  const namaProduk = useMemo(() => Object.fromEntries((pilihan?.data?.produk || []).map((p) => [p.uuid, p.nama])), [pilihan]);

  const teksSasaran = (p) => {
    if (p.sasaran === 'semua') return 'Semua produk';
    const peta = p.sasaran === 'kategori' ? namaKategori : namaProduk;
    const nama = (p.sasaranUuids || []).map((u) => peta[u]).filter(Boolean);
    const sisa = (p.sasaranUuids || []).length - nama.length;
    const label = p.sasaran === 'kategori' ? 'Kategori' : 'Produk';
    return `${label}: ${nama.slice(0, 3).join(', ')}${nama.length > 3 ? ` +${nama.length - 3}` : ''}${sisa > 0 ? `${nama.length ? ', ' : ''}${sisa} lainnya` : ''}`;
  };

  const ubahAktif = async (p) => {
    try {
      await axios.put(`${base()}/promo/${p.uuid}`, keBody({ ...dariPromo(p), aktif: !p.aktif }, superadmin), { withCredentials: true });
      mutate();
      notif.sukses(p.aktif ? 'Promo dinonaktifkan' : 'Promo diaktifkan');
    } catch (e) {
      notif.error(e?.response?.data?.message || 'Gagal mengubah status promo');
    }
  };

  const hapus = async (p) => {
    if (!(await konfirmasi(`Hapus promo "${p.nama}"? Transaksi yang sudah memakainya tetap tercatat.`))) return;
    try {
      await axios.delete(`${base()}/promo/${p.uuid}`, { withCredentials: true });
      mutate();
      notif.sukses('Promo dihapus');
    } catch (e) {
      notif.error(e?.response?.data?.message || 'Gagal menghapus promo');
    }
  };

  const status = (p) => {
    if (!p.aktif) return <Chip size="small" label="Nonaktif" />;
    if (p.berlakuSekarang) return <Chip size="small" color="success" label="Berlaku sekarang" />;
    return <Chip size="small" color="info" variant="outlined" label="Aktif · di luar jadwal" />;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Box>
          <Typography variant="h6">Promo & Kupon</Typography>
          <Typography variant="body2" color="text.secondary">
            Promo otomatis diterapkan sendiri saat transaksi (kasir, menu QR); bila beberapa cocok dipakai satu yang potongannya terbesar. Kupon (kode) diketik kasir
            dan boleh ditumpuk. Promo ulang tahun berlaku untuk pelanggan terdaftar yang dipilih kasir.
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setForm({ open: true, awal: null })}>Promo baru</Button>
      </Box>

      {superadmin && (
        <TextField size="small" select label="Cabang" value={cabang} onChange={(e) => setCabang(e.target.value)} sx={{ minWidth: 220, my: 2 }}>
          <MenuItem value="">Semua cabang</MenuItem>
          {daftarCabang.map((c) => <MenuItem key={c.uuid} value={c.uuid}>{c.namacabang}</MenuItem>)}
        </TextField>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab label="Daftar promo" />
        <Tab label="Laporan pemakaian" />
      </Tabs>
      {tab === 1 && <LaporanPromo cabang={cabang} />}
      {tab === 0 && (
      <>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, my: 2, maxWidth: 560 }}>
        {[['Jumlah promo', r.jumlah ?? 0, 'text.primary'], ['Aktif', r.aktif ?? 0, 'info.main'], ['Berlaku sekarang', r.berlakuSekarang ?? 0, 'success.main']].map(([judul, nilai, warna]) => (
          <Card key={judul} variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">{judul}</Typography>
              <Typography variant="h6" color={warna} fontWeight={700}>{nilai}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error?.response?.data?.code === 'FITUR_TIDAK_TERSEDIA' ? 'Paket cabang ini belum memiliki fitur Promo Otomatis.' : (error?.response?.data?.message || 'Gagal memuat promo')}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Promo</TableCell>
              <TableCell>Potongan</TableCell>
              <TableCell>Berlaku untuk</TableCell>
              <TableCell>Jadwal & syarat</TableCell>
              {superadmin && <TableCell>Cabang</TableCell>}
              <TableCell>Status</TableCell>
              <TableCell align="right">Dipakai</TableCell>
              <TableCell align="right">Aksi</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={8}>Memuat…</TableCell></TableRow>}
            {!isLoading && list.length === 0 && <TableRow><TableCell colSpan={8}>Belum ada promo. Tekan "Promo baru" untuk membuat yang pertama.</TableCell></TableRow>}
            {list.map((p) => (
              <TableRow key={p.uuid} hover sx={{ opacity: p.aktif ? 1 : 0.6 }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={700}>{p.nama}</Typography>
                  {p.tipe === 'kupon' && <Chip size="small" color="secondary" label={`Kupon ${p.kode}`} sx={{ mr: 0.5, mt: 0.5 }} />}
                  {p.tipe === 'ulang_tahun' && <Chip size="small" color="secondary" variant="outlined" label={p.ulangTahun === 'bulan' ? 'Ulang tahun · sebulan' : 'Ulang tahun · hari H'} sx={{ mr: 0.5, mt: 0.5 }} />}
                  {p.deskripsi && <Typography variant="caption" color="text.secondary" display="block">{p.deskripsi}</Typography>}
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{teksPotongan(p)}</TableCell>
                <TableCell>{teksSasaran(p)}</TableCell>
                <TableCell>
                  {teksJadwal(p).map((t, i) => <Typography key={i} variant="caption" display="block">{t}</Typography>)}
                  {p.minBelanja > 0 && <Typography variant="caption" display="block">Min. belanja {rupiah(p.minBelanja)}</Typography>}
                </TableCell>
                {superadmin && <TableCell>{p.semuaCabang ? <Chip size="small" label="Semua cabang" /> : (p.namacabang || '-')}</TableCell>}
                <TableCell>{status(p)}</TableCell>
                <TableCell align="right">
                  {p.dipakai}×{p.batasTotal ? ` / ${p.batasTotal}` : ''}
                  <Typography variant="caption" color="text.secondary" display="block">{rupiah(p.totalPotongan)}</Typography>
                  {p.batasPerPelanggan ? <Typography variant="caption" color="text.secondary" display="block">maks {p.batasPerPelanggan}× per pelanggan</Typography> : null}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {p.bisaDiubah ? (
                    <>
                      <Tooltip title={p.aktif ? 'Nonaktifkan' : 'Aktifkan'}><Switch size="small" checked={p.aktif} onChange={() => ubahAktif(p)} /></Tooltip>
                      <Tooltip title="Ubah"><IconButton size="small" onClick={() => setForm({ open: true, awal: dariPromo(p) })}><EditOutlined fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Hapus"><IconButton size="small" color="error" onClick={() => hapus(p)}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                    </>
                  ) : <Typography variant="caption" color="text.secondary">Dari pusat</Typography>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </>
      )}

      <PromoForm
        open={form.open} awal={form.awal} superadmin={superadmin} daftarCabang={daftarCabang}
        onClose={() => setForm({ open: false, awal: null })} onSimpan={(pesan) => { mutate(); notif.sukses(pesan); }}
      />
    </Box>
  );
};

export default PromoManager;
